import _ from 'lodash';
import PictionJS from 'piction-js';
import queryString from 'query-string';
import { fromJS } from 'immutable';
import appConfig, { updateConfig } from '../appConfig';
import { getKeyPath } from './immutableUtils';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { sessionExpired, updateStoreFromNewConfig } from '../actions/session';
import { retrieveUMOsFailure } from '../actions/umoGallery';

let isWorkbenchValue = false;

const piction = new PictionJS(appConfig.hostURL, {
  videoOriginalIndexForWeb: appConfig.videoOriginalIndexForWeb,
  audioOriginalIndexForWeb: appConfig.audioOriginalIndexForWeb,
});

export const addSURL = (surl) => {
  // hack to add the surl to the PictionJS instance
  piction.session._surl = surl; // eslint-disable-line no-underscore-dangle
};

export const addToken = (token) => {
  // hack to add the token to the PictionJS instance
  piction.session._token = token; // eslint-disable-line no-underscore-dangle
};

export const setCookies = (val) => {
  piction.session._isSurlCookie = val; // eslint-disable-line no-underscore-dangle
};

/**
 * Piction accepts a specific value for language rather than a locale code, so
 * this function deals with that.
 * @param locale
 */
export const getLanguageFromLocale = (locale) => {
  let language;

  switch (locale) {
    case 'en': break;
    case 'fr': language = 'FRENCH'; break;
    default:
      console.warn(
        `Locale ${locale} not recognised while trying to set language for piction calls`
      );
      break;
  }

  return language;
};

export const setLocale = (locale) => {
  piction.setLanguage(getLanguageFromLocale(locale));
};

/**
 * Piction returns a language index on login/validate
 * need to transform it for kaui2 standard
 * @param langIndex
 */
export const getLocaleFromPictionIndex = (langIndex) => {
  if (langIndex === '2') {
    setLocale('fr');
    return 'fr';
  }
  setLocale('en');
  return 'en';
};

/**
 * Provides easier way to store and retrieve the isWorkbench bool from session store. It shouldn't
 * change after initial load plus it is used throughout the app so easier than passing down the
 * value or mapping state to props for components.
 * @param isWorkbenchBool Optional. If set will set the state. If not, then will return current
 * state.
 */
export const isWorkbench = (isWorkbenchBool) => {
  if (isWorkbenchBool === false || isWorkbenchBool === true) {
    isWorkbenchValue = isWorkbenchBool;
  }
  return isWorkbenchValue;
};

/*
 * Required to check if categories/lightboxes should be restricted or not
 * workspaces are always editable if are visible
 * highlights are only editable to the creator, and never to discoverer users
 * @param category- the category to check permissions on
* */
export const isEditable = (category) => {
  // if category of type lightbox(workspace)/set always allow editing
  if (category.get('t') === 'L' || category.get('t') === 'S') {
    return !(category.get('wstype') === 'SET' && !isWorkbench());
  } else if (appConfig.restrictEditing) return category.get('rw') === 'TRUE';

  return true; // Always return true if the appConfig is not on
};

/**
 * Provides easier way to store and retrieve whether edit mode is enabled bool from session store.
 * Edit mode allows discoverer users more edit possibilities
 * (e.g edit the primary picture, and use crop or resize in the umoMenu)
 * It shouldn't change after initial load plus it is used throughout the app so easier than
 * passing down the value or mapping state to props for components.
 */
export const isEditMode = () => (isWorkbench() || appConfig.discovererEditMode);

export const createTemplateURL = (templateName, params, locale) => {
  let paramsQueryString = params ? `&${queryString.stringify(params)}` : '';

  if (locale && getLanguageFromLocale(locale)) {
    paramsQueryString += `&LANGUAGE=${getLanguageFromLocale(locale)}`;
  }
  return `${appConfig.hostURL}!tpl.private_page?&acid=&name=${templateName}${paramsQueryString}`;
};

export default piction;

export const retrieveAndApplyUserConfig = (surl, csrf, dispatch) => {
  if (appConfig.useUserDefinedConfig && csrf) {
    return $.post(`${appConfig.hostURL}!soap.ajaxget?n=KAUI_CONF`, { surl: surl || '', csrf })
      .done((res) => {
        try {
          (() => {
            if (res.indexOf('appConfig') < 0) {
              console
                .warn('remote appConfig does not look like it has been defined correctly. ' +
                  'if remote appConfig is not required, please disable it in the main appConfig ' +
                  'to prevent this message from showing. ' +
                  'if remote appConfig is required, check that the config is defined with ' +
                  '"appConfig = {...".');
            }
            const dat = _.split(res, '-->')[1]; // remove first line
            const userAppConfig = eval(dat); // eslint-disable-line no-eval
            updateConfig({ ...userAppConfig }); // eslint-disable-line no-undef
            piction.setDerivatives({
              vidDeriv: userAppConfig.videoOriginalIndexForWeb,
              audDeriv: userAppConfig.audioOriginalIndexForWeb,
            });
            dispatch(updateStoreFromNewConfig());
          })();
        } catch (err) {
          throw new Error('unable to parse the user defined config', err);
        }
      })
      .fail((res) => {
        if (res.responseText.indexOf('<!--' < 0)) console.warn(res);
        else {
          const dat = _.split(res, '-->')[1]; // remove first line
          const userAppConfig = eval(dat); // eslint-disable-line no-eval
          updateConfig({ ...userAppConfig }); // eslint-disable-line no-undef
          piction.setDerivatives({
            vidDeriv: userAppConfig.videoOriginalIndexForWeb,
            audDeriv: userAppConfig.audioOriginalIndexForWeb,
          });
          dispatch(updateStoreFromNewConfig());
        }
      });
  } return null;
};

export const metaAttach = (meta, umoIDList, isReplace, intl, searchQuery) => {
  const options = [];

  const repeatFields = meta.filter(tag =>
    tag.type === 'RGROUP' || tag.repeatingField === true
  );

  // If not appending, then clear out repeating fields.
  if (isReplace) {
    for (const tag in repeatFields) {
      // Handle rgroup children instead of parent
      if (repeatFields[tag].type === 'RGROUP') {
        const rTags = repeatFields[tag].rgroup;
        for (let i = 0; i < rTags.length; i += 1) {
          options.push(['META_TAG', rTags[i].name]);
          options.push(['DELETE', 'TRUE']);
        }
      } else {
        options.push(['META_TAG', repeatFields[tag].name]);
        options.push(['DELETE', 'TRUE']);
      }
    }
  }

  for (const tag in meta) {
    if (meta[tag].type === 'RGROUP') {
      const rTags = {};
      // Parse out repeating group values
      for (let k = 0; k < meta[tag].value.length; k += 1) {
        const value = meta[tag].value[k];
        for (let i = 0; i < value.length; i += 1) {
          if (!rTags[value[i].name]) rTags[value[i].name] = [];
          rTags[value[i].name].push(value[i].value || '');
        }
      }

      for (const rTag in rTags) {
        for (let k = 0; k < rTags[rTag].length; k += 1) {
          options.push(['META_TAG', rTag]);

          if (!isReplace) options.push(['POSITION_ID', 'APPEND']);
          else options.push(['POSITION_ID', k + 1]);

          options.push(['VALUE', encodeURIComponent(rTags[rTag][k] || '')]);
        }
      }
    } else if (meta[tag].repeatingField) {
      for (let k = 0; k < meta[tag].value.length; k += 1) {
        options.push(['META_TAG', meta[tag].name]);

        if (!isReplace) options.push(['POSITION_ID', 'APPEND']);
        else options.push(['POSITION_ID', (k + 1)]);

        options.push(['VALUE', encodeURIComponent(meta[tag].value[k] || '')]);
      }
    } else {
      options.push(['META_TAG', meta[tag].name]);

      if (!isReplace) {
        // for appending keywords- add comma before new values
        if (meta[tag].valueType && _.toUpper(meta[tag].valueType) === 'KEYWORD') options.push(['SUFFIX_VALUE', `,${encodeURIComponent(meta[tag].value || '')}`]);
        else options.push(['SUFFIX_VALUE', encodeURIComponent(meta[tag].value || '')]);
      } else options.push(['VALUE', encodeURIComponent(meta[tag].value || '')]);
    }
  }

  // if search query exists- use that, and exclude umos on the list ("deselected" umos)
  const umoArray = _.isArray(umoIDList) ? umoIDList : [umoIDList];
  if (searchQuery) {
    options.push(['SEARCH', searchQuery]);
    for (let i = 0; i < umoArray.length; i += 1) {
      options.push(['EXCLUDE_UMO_ID', umoArray[i]]);
    }
  } else {
    // Insert all umos into webservice call
    for (let i = 0; i < umoArray.length; i += 1) {
      options.push(['UMO_ID', umoArray[i]]);
    }
  }

  return piction.request('Metadata_Update_V2', options).then((res) => {
    for (let i = 0; i < res.a.RETURN_MESSAGE.length; i += 1) {
      if (res.a.RETURN_MESSAGE[i]._ !== 'Updated') {
        if (res.a.RETURN_MESSAGE.length > 1) {
          throw new Error(
            intl ? intl.formatMessage({ id: 'metaAttach.umosExpectedError' }) :
              'There was at least one error while performing metadata attach. Please try again.'
          );
        }
        throw new Error(res.a.RETURN_MESSAGE[i]);
      }
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    }

    return { success: true, res };
  });
};

export const emailUMOsViaSearchQuery =
  (lightboxID, searchQuery, fromName, fromEmail, toEmail, message, isPortal, options) => {
    let callArgs;
    let reqType = 'email_image';

    if (isPortal) {
      reqType = 'mng_portal';
      callArgs = Object.assign({
        SEARCH: searchQuery,
        ACTION: 'CREATE_WORKSPACE',
        USERNAME: fromName,
        PASSWORD: toEmail,
        EMAIL_ADDRESS: toEmail,
        MESSAGE: message,
        WORKSPACE: lightboxID,
        TEMPLATE: 'KAUI2.Portal_Email',
      }, options);
      if (appConfig.portalShareUsername) {
        callArgs.BASE_ACCOUNT_ON_USERNAME = appConfig.portalShareUsername;
      }
    } else {
      callArgs = Object.assign({
        SEARCH: searchQuery,
        DESC: fromName,
        FROM_EMAIL: fromEmail,
        TO_EMAIL: toEmail,
        MESSAGE: message,
        DOWNLOAD_WHAT: 'WEB',
        EMAIL_TEMPLATE: 'download_image.html',
      }, options);
    }

    return piction.request(reqType, callArgs).then((res) => {
      if (res && res.a && res.a.RETURN_MESSAGE && _.isArray(res.a.RETURN_MESSAGE)) {
        if (res.a.RETURN_MESSAGE[0] === 'Email Sent') {
          return { success: true };
        }

        throw new Error(res.a.RETURN_MESSAGE[0]);
      } else if (res.a.SUCCESS && _.isArray(res.a.SUCCESS)) {
        if (_.includes(res.a.SUCCESS[0]._, 'Acct created')) {
          return { success: true };
        }
      } else {
        if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
          throw new Error(res);
        }
        throw new Error('Invalid response');
      }
    });
  };

export const emailUMOsViaLightbox = (lightboxID, fromName, fromEmail, toEmail, message, isPortal) =>
  // called via exports so it can be mocked in tests
  exports.emailUMOsViaSearchQuery(
      lightboxID,
    `IN_CATEGORY:"${lightboxID}"`,
    fromName,
    fromEmail,
    toEmail,
    message,
    isPortal,
  );

export const relateUMOsPeer = (options) => {
  const callArgs = Object.assign({
  }, options);

  return piction.request('RELATE_BY', callArgs).then((res) => {
    if (res && res.RETURN_MESSAGE) {
      return res.RETURN_MESSAGE;

      throw new Error(res.RETURN_MESSAGE);
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    } else {
      throw new Error('Invalid response');
    }
  });
};

export const rateUMO = (options) => {
  const callArgs = Object.assign({
    UPDATE_INCLUDE_SUMMARY: 'TRUE',
    INCLUDE_SUMMARY: 'TRUE',
  }, options);

  return piction.request('NWS_RATING', callArgs).then((res) => {
    if (res && res.RETURN_MESSAGE) {
      return res.RETURN_MESSAGE;

      throw new Error(res.RETURN_MESSAGE);
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    } else {
      throw new Error('Invalid response');
    }
  });
};

export const getUserList = (options) => {
  const callArgs = Object.assign({
    SORT: 'USERNAME',
  }, options);

  return piction.request('Profile', callArgs).then((res) => {
    if (res && res.a && res.a.p && _.isArray(res.a.p)) {
      return res.a.p;

      throw new Error(res.a);
    }
    if (res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) {
      throw new Error(res);
    } else {
      throw new Error('Invalid response');
    }
  });
};

export const moveCategory = (categoryIDToMove, destinationCategoryID, destinationCollectionID) => {
  const callArgs = {
    FROM_COLLECTION: 0,
    TO_COLLECTION: destinationCollectionID,
    CURRENT_CATEGORY_ID: categoryIDToMove,
    PARENT_NEW_CATEGORY_ID: destinationCategoryID,
    ENFORCE_CHECKS: appConfig.restrictEditing ? 'CHECK_TO_CTG' : 'NONE',
    ACTION: 'MOVE_FOLDER',
  };

  return piction.request('UTILITY', callArgs).then((res) => {
    let returnMessage;

    try {
      if (res.a.RESULT && _.isArray(res.a.RESULT)) {
        returnMessage = res.a.RESULT[0];
      } else {
        // Hack to work with different error types returning from server
        if (res.a.RETURN_MESSAGE[0]._ && res.a.RETURN_MESSAGE[0]._ ===
          'Session does not have insert, update or alter on target category') {
          returnMessage = 'utils.piction.moveCategoryPermissionError';
        } else {
          returnMessage = res.a.RETURN_MESSAGE[0];
        }
      }
    } catch (err) {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        returnMessage = SESSION_EXPIRED_MESSAGE;
      } else {
        returnMessage = 'utils.piction.moveCategoryGenericError';
      }
    }

    if (returnMessage === 'MOVED') {
      return undefined;
    }

    throw new Error(returnMessage);
  });
};

/**
 * When retrieving categories from Piction we usually return with only a depth of 1 so that it's
 * faster. What this utility does is merge child category query data back in to the original
 * category query.
 *
 * For example we do a CategoriesXML category retrieval for the root categories which returns
 * something like this:
 *
 * [
 * { i: '001', n: 'root cat 1', ct: [], ch: 'FALSE' },
 * { i: '002', n: 'root cat 2', ct: [], ch: 'TRUE' },
 * ]
 *
 * We then want to retrieve the children for category 002 which returns this:
 *
 * [
 * { i: '0021', n: 'cat 2 1', ct: [], ch: 'FALSE' },
 * { i: '0022', n: 'cat 2 2', ct: [], ch: 'FALSE' },
 * ]
 *
 * This utility will merge the children data with the original (root level) category data:
 *
 * [
 * { i: '001', n: 'root cat 1', ct: [], ch: 'FALSE' },
 * { i: '002', n: 'root cat 2', ct: [{ i: '0021', ... }, { i: '0022', ... }], ch: 'TRUE' },
 * ]
 *
 * See the tests for practical use.
 *
 * @param categoryList This is essentially the category response from piction where the children
 * will be inserted to
 * @param categoryIDToUpdate Specific category ID that the children should be inserted to
 * @param updatedChildrenList Array of children to insert
 */
// todo: this could probably be used for the category/workspace retrieval (see respective reducers)
export const updateCategoryChildren = (categoryList, categoryIDToUpdate, updatedChildrenList) => {
  const keyPath = getKeyPath(categoryList, 'i', categoryIDToUpdate, 'ct');

  if (keyPath) {
    // create a lookup of the current children so they can be easily found
    const currentChildrenLookup = {};
    categoryList.getIn([...keyPath, 'ct']).forEach((item, index) => {
      currentChildrenLookup[item.get('i')] = index;
    });

    // go through the updated children list and copy over its previously returned children, if any.
    const newChildrenList = fromJS(updatedChildrenList).map((item) => {
      if (Object.prototype.hasOwnProperty.call(currentChildrenLookup, item.get('i'))) {
        const currentChildIndex = currentChildrenLookup[item.get('i')];
        return item.set('ct', categoryList.getIn([...keyPath, 'ct', currentChildIndex, 'ct']));
      }
      return item;
    });

    return categoryList.setIn(
      [...keyPath, 'ct'],
      newChildrenList
    );
  }
  return categoryList;
};

export const getUMOShareLink = (dispatch, umoID, options) => {
  const callArgs = Object.assign({

  }, options);

  return piction.getUMOs(`UMO_ID:"${umoID}"`, callArgs).then((res) => {
    if (res.res.a.r) return res.umoList.all[0];
  })
     .catch((err) => {
       if (err.message && (err.message.includes(SESSION_EXPIRED_MESSAGE)
        || err.message.includes('You are not logged in'))) {
         dispatch(sessionExpired(err));
       } else {
         dispatch(retrieveUMOsFailure(err));
       }
       return err;
     });
};

export const sendUsageData = (umoID, data, dispatch) => {
  const callArgs = Object.assign({
    UMO_ID: umoID,
  }, data);

  return piction.request('DATA_IN_BASKET', callArgs)
    .then((res) => {
      console.log('data usage', res);
      return res;
    })
    .catch((err) => {
      if (err.message && (err.message.includes(SESSION_EXPIRED_MESSAGE)
        || err.message.includes('You are not logged in'))) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveUMOsFailure(err));
      }
      return err;
    });
};



// WEBPACK FOOTER //
// ./src/js/utils/piction.js